/**
 * Admin Portal Layout
 * Professional sidebar layout using shadcn/ui Sidebar components
 */

import { Outlet, Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  LayoutTemplate,
  Building2,
  Key,
  Users,
  Settings,
  ScrollText,
  LogOut,
  Bell,
  Loader2,
  ChevronRight,
  ChevronsUpDown,
  Shield,
  User,
  Activity,
  MessageSquareText,
  Mail,
  ContactRound,
  CalendarDays,
} from "lucide-react";
import { useEffect } from "react";
import { useSession, signOut } from "../lib/auth";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { LanguageSelector } from "./LanguageSelector";
import { ImpersonationBanner } from "./ImpersonationBanner";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
  Separator,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarFallback,
  Badge,
  useToast,
} from "@jourfx/ui";
import type { UserRole } from "@jourfx/types/users";

// Navigation items with role requirements
type AdminRole = Extract<UserRole, "administrator" | "super_admin">;

const allNavigationItems = [
  {
    key: "dashboard",
    href: "/",
    icon: LayoutDashboard,
    roles: ["administrator", "super_admin"] as AdminRole[],
  },
  {
    key: "users",
    href: "/users",
    icon: Users,
    roles: ["administrator", "super_admin"] as AdminRole[],
  },
  {
    key: "appointments",
    href: "/appointments",
    icon: CalendarDays,
    roles: ["administrator", "super_admin"] as AdminRole[],
  },
  {
    key: "versicherte",
    href: "/versicherte",
    icon: ContactRound,
    roles: ["administrator", "super_admin"] as AdminRole[],
  },
  {
    key: "templates",
    href: "/templates",
    icon: LayoutTemplate,
    roles: ["administrator", "super_admin"] as AdminRole[],
  },
  {
    key: "feedback",
    href: "/feedback",
    icon: MessageSquareText,
    roles: ["administrator", "super_admin"] as AdminRole[],
  },
  {
    key: "organizations",
    href: "/organizations",
    icon: Building2,
    roles: ["super_admin"] as AdminRole[],
  },
  {
    key: "licenses",
    href: "/licenses",
    icon: Key,
    roles: ["super_admin"] as AdminRole[],
  },
  {
    key: "admins",
    href: "/admins",
    icon: Shield,
    roles: ["administrator", "super_admin"] as AdminRole[],
  },
  {
    key: "auditLogs",
    href: "/audit",
    icon: ScrollText,
    roles: ["super_admin"] as AdminRole[],
  },
  {
    key: "system",
    href: "/system",
    icon: Activity,
    roles: ["administrator", "super_admin"] as AdminRole[],
  },
  {
    key: "settings",
    href: "/settings",
    icon: Settings,
    roles: ["super_admin"] as AdminRole[],
  },
  {
    key: "emailTemplates",
    href: "/email-templates",
    icon: Mail,
    roles: ["super_admin"] as AdminRole[],
  },
];

function getNavLabel(
  t: (key: string) => string,
  key: string
): string {
  switch (key) {
    case "dashboard":
      return t("nav.dashboard");
    case "users":
      return t("nav.users");
    case "versicherte":
      return t("nav.versicherte");
    case "appointments":
      return t("nav.appointments");
    case "templates":
      return t("nav.templates");
    case "feedback":
      return t("nav.feedback");
    case "organizations":
      return t("nav.organizations");
    case "licenses":
      return t("nav.licenses");
    case "admins":
      return t("nav.admins");
    case "auditLogs":
      return t("nav.auditLogs");
    case "system":
      return t("nav.system");
    case "settings":
      return t("nav.settings");
    case "emailTemplates":
      return t("nav.emailTemplates");
    default:
      return key;
  }
}

function getAdminRoleLabel(
  t: (key: string) => string,
  role?: string
): string {
  if (role === "super_admin") return t("roles.super_admin");
  return t("roles.administrator");
}

function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: session, isSuperAdmin } = useSession();
  const { state } = useSidebar();
  const { t } = useTranslation("common");

  // Filter navigation based on role
  const navigationItems = allNavigationItems.filter((item) => {
    if (isSuperAdmin) return true;
    return item.roles.includes("administrator");
  });

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    await signOut();
    navigate({ to: "/login" });
  };

  const userInitials = session?.user?.name
    ?.split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      {/* Header with Logo */}
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Link to="/">
                <img
                  src="/logo_jf.png"
                  alt="JourFx"
                  className="size-8 object-contain"
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-sidebar-foreground">{t('app.title')}</span>
                  <span className="truncate text-xs text-sidebar-foreground/80">
                    {t('app.subtitle')}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Main Navigation */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/80">{t('nav.navigation')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => {
                const active = isActive(item.href);
                const name = getNavLabel(t, item.key);
                return (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={name}
                    >
                      <Link to={item.href}>
                        <item.icon className="size-4" />
                        <span>{name}</span>
                        {active && (
                          <ChevronRight className="ml-auto size-4 text-sidebar-foreground/80" />
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer with User Menu */}
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="size-9 ring-2 ring-sidebar-primary/30">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold text-sidebar-foreground">
                      {session?.user?.name || t('user')}
                    </span>
                    <span className="truncate text-xs text-sidebar-foreground/70">
                      {session?.user?.email || ""}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4 text-sidebar-foreground/50" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side={state === "collapsed" ? "right" : "top"}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="size-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">
                        {session?.user?.name || t('user')}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {session?.user?.email || ""}
                      </span>
                      {session?.adminUser?.organizationName && (
                        <span className="truncate text-xs text-muted-foreground">
                          {session.adminUser.organizationName}
                        </span>
                      )}
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {getAdminRoleLabel(t, session?.adminUser?.role)}
                    </Badge>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/account" className="cursor-pointer">
                    <User className="mr-2 size-4" />
                    {t('nav.account')}
                  </Link>
                </DropdownMenuItem>
                {session?.adminUser?.role === "super_admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer">
                      <Settings className="mr-2 size-4" />
                      {t('nav.settings')}
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive cursor-pointer"
                >
                  <LogOut className="mr-2 size-4" />
                  {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      {/* Rail for resize handle */}
      <SidebarRail />
    </Sidebar>
  );
}

function TopHeader() {
  const { t } = useTranslation("common");
  const { toast } = useToast();

  const handleNotificationsClick = () => {
    toast({
      title: t('notifications'),
      description: t('notificationsComingSoon'),
    });
  };

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />

      {/* Breadcrumb area - can be extended */}
      <div className="flex-1" />

      {/* Right side actions */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <Button variant="ghost" size="icon" className="relative" onClick={handleNotificationsClick}>
          <Bell className="size-4" />
          <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-destructive" />
          <span className="sr-only">{t('notifications')}</span>
        </Button>

        {/* Environment indicator - uses design system teal */}
        <Badge
          variant="outline"
          className="hidden bg-primary/10 text-primary border-primary/20 sm:inline-flex"
        >
          <Shield className="mr-1 size-3" />
          {t('app.subtitle')}
        </Badge>
      </div>
    </header>
  );
}

export function AdminLayout() {
  const navigate = useNavigate();
  const { session, isPending, isAuthenticated, provisioningFailed, errorMessage } = useAdminAuth();
  const { t } = useTranslation("common");

  // Redirect to login if not authenticated and not loading
  useEffect(() => {
    if (!isPending && !isAuthenticated && !provisioningFailed) {
      navigate({ to: "/login" });
    }
  }, [isAuthenticated, isPending, provisioningFailed, navigate]);

  // Show loading while checking auth or provisioning
  if (isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <img
            src="/logo_jf.png"
            alt="JourFx"
            className="h-12 w-auto"
          />
          <Loader2 className="size-6 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">{t('table.loading')}</span>
        </div>
      </div>
    );
  }

  // Show error if provisioning failed (user not authorized for admin portal)
  if (provisioningFailed) {
    const handleLogout = async () => {
      await signOut();
      navigate({ to: "/login" });
    };

    return (
      <div className="flex min-h-svh items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 max-w-md text-center p-6">
          <img src="/logo_jf.png" alt="JourFx" className="h-12 w-auto" />
          <h2 className="text-lg font-semibold text-destructive">
            {errorMessage === "inactive"
              ? t("auth.accountInactive")
              : t("auth.notAuthorized")}
          </h2>
          <Button variant="outline" onClick={handleLogout}>
            {t("auth.toLogin")}
          </Button>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated || !session) {
    return null;
  }

  return (
    <SidebarProvider>
      <ImpersonationBanner />
      <AppSidebar />
      <SidebarInset>
        <TopHeader />
        <main className="flex-1 overflow-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </SidebarInset>
      <LanguageSelector />
    </SidebarProvider>
  );
}
